import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { addNotification } from '../../store/slices/notificationSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { usePageMetadata } from '../../hooks/usePageMetadata';

import { Badge } from '../../components/ui/Badge';
import {
  User,
  Mail,
  Phone,
  Camera,
  CreditCard,
  Building2,
  FileText,
  Upload,
  X,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';
import UserAvatar from '../../components/common/UserAvatar';
import {
    useGetDealerProfileQuery,
    useUpdateDealerProfileMutation,
    useUpdateDealerDocumentsMutation,
    useResubmitDealerVerificationMutation,
} from '../../store/slices/apiSlice';

const profileSchema = yup.object({
  name: yup.string().required('Name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  mobile_number: yup.string().optional(),
  business_name: yup.string().required('Business name is required'),
  business_address: yup.string().optional(),
  business_phone: yup.string().optional(),
  country_code: yup.string().optional(),
  currency_symbol: yup.string().optional(),
});

type ProfileFormData = yup.InferType<typeof profileSchema>;

const DealerProfile: React.FC = () => {
  const { user, token } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<'profile' | 'documents'>('profile');
  const [documentFiles, setDocumentFiles] = useState<{
    business_license?: File;
    id_proof?: File;
    address_proof?: File;
    tax_certificate?: File;
    other?: File[];
  }>({});

  // Set page metadata
  usePageMetadata('profile');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Use RTK Query for dealer profile
  const { data: profileData, refetch, isLoading: isProfileLoading } = useGetDealerProfileQuery(undefined, {
    skip: !token,
  });
  const [updateProfile, { isLoading: isUpdating }] = useUpdateDealerProfileMutation();
  const [updateDocuments, { isLoading: isUpdatingDocuments }] = useUpdateDealerDocumentsMutation();
  const [resubmitVerification, { isLoading: isResubmitting }] = useResubmitDealerVerificationMutation();
  
  const profile = profileData?.data;

  const profileForm = useForm<ProfileFormData>({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name: '',
      email: '',
      mobile_number: '',
      business_name: '',
      business_address: '',
      business_phone: '',
      country_code: '',
      currency_symbol: '',
    },
  });

  useEffect(() => {
    if (profile) {
      profileForm.reset({
        name: profile.name || '',
        email: profile.email || '',
        mobile_number: profile.mobile_number || '',
        business_name: profile.business_name || '',
        business_address: profile.business_address || '',
        business_phone: profile.business_phone || '',
        country_code: profile.country_code || '',
        currency_symbol: profile.currency_symbol || '',
      });
    }
  }, [profile, profileForm]);

  const handleProfileUpdate = async (data: ProfileFormData) => {
    try {
      const formData = new FormData();

      formData.append('name', data.name);
      if (data.mobile_number) formData.append('mobile_number', data.mobile_number);
      if (data.business_name) formData.append('business_name', data.business_name);
      if (data.business_address) formData.append('business_address', data.business_address);
      if (data.business_phone) formData.append('business_phone', data.business_phone);
      if (data.country_code) formData.append('country_code', data.country_code);
      if (data.currency_symbol) formData.append('currency_symbol', data.currency_symbol);

      // Add file only if selected
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      await updateProfile(formData).unwrap();
      toast.success('Profile updated successfully!');
      refetch();
      setSelectedFile(null);
      dispatch(addNotification({
        title: 'Profile Updated',
        message: 'Your profile information has been successfully updated.',
        type: 'success',
      }));
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to update profile');
    }
  };

  const handleDocumentsUpdate = async () => {
    try {
      const formData = new FormData();

      if (documentFiles.business_license) {
        formData.append('business_license', documentFiles.business_license);
      }
      if (documentFiles.id_proof) {
        formData.append('id_proof', documentFiles.id_proof);
      }
      if (documentFiles.address_proof) {
        formData.append('address_proof', documentFiles.address_proof);
      }
      if (documentFiles.tax_certificate) {
        formData.append('tax_certificate', documentFiles.tax_certificate);
      }
      if (documentFiles.other) {
        documentFiles.other.forEach(file => {
          formData.append('other', file);
        });
      }

      await updateDocuments(formData).unwrap();
      toast.success('Documents updated successfully!');
      refetch();
      setDocumentFiles({});
      dispatch(addNotification({
        title: 'Documents Updated',
        message: 'Your documents have been successfully updated.',
        type: 'success',
      }));
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to update documents');
    }
  };

  const handleResubmit = async () => {
    try {
      await resubmitVerification().unwrap();
      toast.success('Application resubmitted successfully! Please wait for admin review.');
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to resubmit application');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const TabButton: React.FC<{
    icon: React.ReactNode;
    label: string;
    isActive: boolean;
    onClick: () => void;
  }> = ({ icon, label, isActive, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center space-x-2 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm font-medium transition-colors ${isActive
        ? 'bg-blue-100 text-blue-700 border border-blue-200'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
        }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600 text-sm">Please log in to view your profile</div>
      </div>
    );
  }

  if (isProfileLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-7xl mx-auto px-4 py-6 bg-white min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
            title="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Dealer Profile</h1>
            <p className="text-sm text-gray-600">Manage your account information and preferences</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* User Info Card */}
        <div className="lg:col-span-1">
          <Card className="p-4">
            <div className="text-center space-y-4">
              {/* Avatar Section */}
              <div className="relative mx-auto">
                <UserAvatar 
                  src={
                    selectedFile
                      ? URL.createObjectURL(selectedFile)
                      : profile?.profile_image
                  }
                  name={profile?.name || user?.name}
                  size="xl"
                  className="mx-auto border-4 border-white shadow-lg"
                />
                <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-1.5 rounded-full hover:bg-blue-700 transition-colors cursor-pointer shadow-lg">
                  <Camera className="h-3 w-3" />
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </label>
              </div>

              {/* User Details */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  {profile?.name || user?.name || 'Dealer Name'}
                </h3>
                <p className="text-sm text-gray-600">
                  {profile?.email || user?.email || 'dealer@example.com'}
                </p>
                <div className="flex justify-center gap-2 flex-wrap">
                  <Badge variant="secondary" className="text-xs bg-white text-gray-800 border-gray-300">
                    {profile?.dealer_type === 'car' ? 'Car Dealer' : 'House Dealer'}
                  </Badge>
                  {profile?.verification_status === 'approved' && (
                    <Badge variant="success" className="text-xs">Approved</Badge>
                  )}
                  {profile?.verification_status === 'pending' && (
                    <Badge variant="warning" className="text-xs">Pending</Badge>
                  )}
                  {profile?.verification_status === 'rejected' && (
                    <Badge variant="danger" className="text-xs">Rejected</Badge>
                  )}
                </div>
                {profile?.rejection_reason && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    <strong>Rejection Reason:</strong> {profile.rejection_reason}
                  </div>
                )}
              </div>

              {/* File Upload Info */}
              <div className="text-xs text-gray-500 space-y-1">
                <p>Click camera icon to change profile picture</p>
                <p>JPG, GIF or PNG. 1MB max.</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2">
          <Card className="p-4">
            {/* Tab Navigation */}
            <div className="flex flex-wrap gap-2 mb-6">
              <TabButton
                icon={<User className="w-4 h-4" />}
                label="Profile Info"
                isActive={activeTab === 'profile'}
                onClick={() => setActiveTab('profile')}
              />
              <TabButton
                icon={<FileText className="w-4 h-4" />}
                label="Documents"
                isActive={activeTab === 'documents'}
                onClick={() => setActiveTab('documents')}
              />
            </div>

            {/* Tab Content */}
            <div className="space-y-4">
              {/* Profile Info Tab */}
              {activeTab === 'profile' && (
                <form onSubmit={profileForm.handleSubmit(handleProfileUpdate)} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Full Name *
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          {...profileForm.register('name')}
                          placeholder="Enter your full name"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {profileForm.formState.errors.name && (
                        <p className="text-xs text-red-500 mt-1">{profileForm.formState.errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="email"
                          {...profileForm.register('email')}
                          disabled
                          placeholder="Enter your email"
                          className="w-full pl-10 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-500 cursor-not-allowed text-sm"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Mobile Number
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="tel"
                          {...profileForm.register('mobile_number')}
                          placeholder="Enter mobile number"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Business Name *
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          {...profileForm.register('business_name')}
                          placeholder="Enter business name"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      {profileForm.formState.errors.business_name && (
                        <p className="text-xs text-red-500 mt-1">{profileForm.formState.errors.business_name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Business Phone
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="tel"
                          {...profileForm.register('business_phone')}
                          placeholder="Enter business phone"
                          className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Business Address
                      </label>
                      <textarea
                        {...profileForm.register('business_address')}
                        rows={3}
                        placeholder="Enter business address"
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Country Code
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          {...profileForm.register('country_code')}
                          placeholder="e.g., NG, US, UK"
                          className="w-full pl-3 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Optional: Country code for currency</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Currency Symbol
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          {...profileForm.register('currency_symbol')}
                          placeholder="e.g., $, ₦, £"
                          className="w-full pl-3 pr-3 py-2 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Sticky bottom buttons */}
                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(-1)}
                      className="w-full sm:w-auto text-sm px-4 py-2"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isUpdating}
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                    >
                      {isUpdating ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (
                <div className="space-y-4">
                  {/* Verification Status Alert */}
                  {profile?.verification_status === 'rejected' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-red-900 mb-1">Account Rejected</h3>
                          {profile.rejection_reason && (
                            <p className="text-xs text-red-700 mb-2">{profile.rejection_reason}</p>
                          )}
                          <p className="text-xs text-red-700">
                            Please update your profile and documents, then resubmit for verification.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {profile?.verification_status === 'pending' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-yellow-900 mb-1">Verification Pending</h3>
                          <p className="text-xs text-yellow-700">
                            Your account is under review. You can view your profile but cannot create listings until approved.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {profile?.verification_status === 'approved' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                        <div className="flex-1">
                          <h3 className="text-sm font-semibold text-green-900 mb-1">Account Approved</h3>
                          <p className="text-xs text-green-700">
                            Your account has been verified. You can now create and manage listings.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Current Documents */}
                  {profile?.documents && profile.documents.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Current Documents</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {profile.documents.map((doc) => (
                          <div key={doc.document_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <FileText className="w-5 h-5 text-gray-500" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{doc.document_name}</p>
                              <p className="text-xs text-gray-500 capitalize">{doc.document_type.replace('_', ' ')}</p>
                            </div>
                            <a
                              href={doc.document_path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 text-sm"
                            >
                              View
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload New Documents */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Update Documents</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Business License
                        </label>
                        {documentFiles.business_license ? (
                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span className="flex-1 text-sm text-gray-700 truncate">{documentFiles.business_license.name}</span>
                            <button
                              type="button"
                              onClick={() => setDocumentFiles(prev => {
                                const newFiles = { ...prev };
                                delete newFiles.business_license;
                                return newFiles;
                              })}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex items-center justify-center w-full p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                            <Upload className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-xs text-gray-600">Upload</span>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  setDocumentFiles(prev => ({ ...prev, business_license: e.target.files![0] }));
                                }
                              }}
                            />
                          </label>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          ID Proof
                        </label>
                        {documentFiles.id_proof ? (
                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span className="flex-1 text-sm text-gray-700 truncate">{documentFiles.id_proof.name}</span>
                            <button
                              type="button"
                              onClick={() => setDocumentFiles(prev => {
                                const newFiles = { ...prev };
                                delete newFiles.id_proof;
                                return newFiles;
                              })}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex items-center justify-center w-full p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                            <Upload className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-xs text-gray-600">Upload</span>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  setDocumentFiles(prev => ({ ...prev, id_proof: e.target.files![0] }));
                                }
                              }}
                            />
                          </label>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Address Proof
                        </label>
                        {documentFiles.address_proof ? (
                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span className="flex-1 text-sm text-gray-700 truncate">{documentFiles.address_proof.name}</span>
                            <button
                              type="button"
                              onClick={() => setDocumentFiles(prev => {
                                const newFiles = { ...prev };
                                delete newFiles.address_proof;
                                return newFiles;
                              })}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex items-center justify-center w-full p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                            <Upload className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-xs text-gray-600">Upload</span>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  setDocumentFiles(prev => ({ ...prev, address_proof: e.target.files![0] }));
                                }
                              }}
                            />
                          </label>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Tax Certificate
                        </label>
                        {documentFiles.tax_certificate ? (
                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                            <FileText className="w-4 h-4 text-gray-500" />
                            <span className="flex-1 text-sm text-gray-700 truncate">{documentFiles.tax_certificate.name}</span>
                            <button
                              type="button"
                              onClick={() => setDocumentFiles(prev => {
                                const newFiles = { ...prev };
                                delete newFiles.tax_certificate;
                                return newFiles;
                              })}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <label className="flex items-center justify-center w-full p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                            <Upload className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-xs text-gray-600">Upload</span>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  setDocumentFiles(prev => ({ ...prev, tax_certificate: e.target.files![0] }));
                                }
                              }}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
                    {profile?.verification_status === 'rejected' && (
                      <Button
                        type="button"
                        onClick={handleResubmit}
                        disabled={isResubmitting}
                        className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-sm px-4 py-2"
                      >
                        {isResubmitting ? 'Resubmitting...' : 'Resubmit for Verification'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      onClick={handleDocumentsUpdate}
                      disabled={isUpdatingDocuments || Object.keys(documentFiles).length === 0}
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-sm px-4 py-2"
                    >
                      {isUpdatingDocuments ? 'Updating...' : 'Update Documents'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DealerProfile;
